import React, { useState, useRef } from 'react';
import {
  Box,
  ToggleButton,
  Popover,
  MenuItem,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import { SvgIconComponent } from '@mui/icons-material';
import {AnimatedLayerName} from "./AnimatedLayer.tsx";

interface ToolbarButtonPopoverProps {
  id: string;
  name: string;
  enabled: boolean;
  icon: string | SvgIconComponent | React.ReactNode;
  toggledOn: boolean;
  options: {
    id: string;
    label: React.ReactNode;
    icon?: string | SvgIconComponent;
    onClick: () => void;
    divider?: boolean;
    selected?: boolean;
  }[];
  onToggle?: () => void;
  onClick?: () => void; // Alias for onToggle, used by LayerControl
  stayOpenAfterSelection?: boolean;
  customFooter?: React.ReactNode;
  labelSide?: string;
  openOnHover?: boolean;
  hideLabel?: boolean;
}

const ToolbarButtonPopover: React.FC<ToolbarButtonPopoverProps> = ({
  id,
  name,
  enabled,
  toggledOn,
  icon,
  options,
  onToggle,
  onClick,
  stayOpenAfterSelection = false,
  customFooter,
  labelSide,
  openOnHover = false,
  hideLabel = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const scheduleClose = () => {
    closeTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 300);
  };

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  
  // Use onClick if provided, otherwise fall back to onToggle
  const toggleHandler = onClick || onToggle;

  const renderIcon = () => {
    if (typeof icon === 'string') {
      return <Icon>{icon}</Icon>;
    }
    if (typeof icon === 'function') {
      const IconComponent = icon as SvgIconComponent;
      return <IconComponent />;
    }
    return icon;
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget as HTMLButtonElement);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (toggleHandler) {
      toggleHandler();
    } else {
      handlePopoverOpen(event);
    }
  };



  const open = Boolean(anchorEl);
  const [hoveredLayer, setHoveredLayer] = useState<string | null>(null);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        margin: '10px',
      }}
      onMouseEnter={(e) => {
        setHoveredLayer(id);
        if (openOnHover && enabled) {
          cancelClose();
          setAnchorEl(e.currentTarget.querySelector('button') as HTMLButtonElement);
        }
      }}
      onMouseLeave={() => {
        setHoveredLayer(null);
        if (openOnHover) {
          scheduleClose();
        }
      }}
    >
      <ToggleButton
        ref={buttonRef}
        disabled={!enabled}
        value={id}
        aria-label={id}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        sx={{
          color: toggledOn ? '#fff' : 'gray',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: enabled ? '#00d4ad' : '#aaa',
          '&:hover': {
            backgroundColor: '#faad17',
          },
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        }}
        onClick={handleButtonClick}
      >
        <Box
          onClick={(event) => {
            if (toggleHandler && !openOnHover) {
              event.stopPropagation();
              handlePopoverOpen(event);
            }
          }}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
        {renderIcon()}
      </Box>
      </ToggleButton>
      {hoveredLayer === id && !hideLabel && !open && (
          <AnimatedLayerName side={labelSide ?? 'left'}>
            {name}
          </AnimatedLayerName>
      )}
      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={openOnHover
          ? { vertical: 'top', horizontal: 'center' }
          : { vertical: 'center', horizontal: 'right' }
        }
        transformOrigin={openOnHover
          ? { vertical: 'bottom', horizontal: 'center' }
          : { vertical: 'center', horizontal: 'left' }
        }
        onClose={handlePopoverClose}
        disableRestoreFocus
        {...(openOnHover ? {
          sx: { pointerEvents: 'none', mt: -1 },
          slotProps: {
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
              sx: {
                pointerEvents: 'auto',
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                color: '#fff',
                borderRadius: 1,
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
                mb: 1,
              },
            },
          },
        } : {})}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              option.onClick();
              if (!stayOpenAfterSelection) {
                handlePopoverClose();
              }
            }}
            sx={{
              backgroundColor: option.selected
                ? (openOnHover ? 'rgba(255,255,255,0.1)' : '#e0f7fa')
                : 'transparent',
              '&:hover': {
                backgroundColor: openOnHover ? 'rgba(255,255,255,0.15)' : (option.selected ? '#b2ebf2' : ''),
              },
              color: openOnHover ? '#fff' : 'inherit',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.icon && (
                typeof option.icon === 'string'
                  ? <Icon sx={{ fontSize: 18, color: openOnHover ? 'rgba(255,255,255,0.7)' : 'inherit' }}>{option.icon}</Icon>
                  : (() => { const OptIcon = option.icon as SvgIconComponent; return <OptIcon sx={{ fontSize: 18, color: openOnHover ? 'rgba(255,255,255,0.7)' : 'inherit' }} />; })()
              )}
              {option.label}
            </Box>
            {option.selected && (
              <Icon sx={{ fontSize: 16, marginLeft: 1, color: openOnHover ? '#00d4ad' : 'inherit' }}>check</Icon>
            )}
          </MenuItem>
        ))}
        {customFooter && (
          <Box sx={{ borderTop: '1px solid #eee', mt: 1 }}>
            {customFooter}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default ToolbarButtonPopover;
